// Ключі КЕП нового формату (PKCS#12, ДСТУ 7624/7564): PBES2 = PBKDF2 з KMAC «Купина»-256 + «Калина»-256 CBC.
// jkurwa знає лише ГОСТ 28147 (Key-6.dat, старі .pfx/.jks), тож такі контейнери розшифровуємо тут і віддаємо їй
// вже відкриті PKCS#8. Без залежностей від Node — працює і в браузері.
import { Kalyna256, decryptCBC } from "@li0ard/kalyna";
import { Kupyna256, KupynaKMAC256 } from "@li0ard/kupyna";

const OID = {
  shroudedKeyBag: "1.2.840.113549.1.12.10.1.2", pbes2: "1.2.840.113549.1.5.13", pbkdf2: "1.2.840.113549.1.5.12",
  kmacKupyna256: "1.2.804.2.1.1.1.1.2.2.4", kalyna256Cbc: "1.2.804.2.1.1.1.1.1.3.5.2",
};

// Мінімальний DER: { tag, body, children? } — конструктивні елементи розбираємо рекурсивно
function der(buf, pos = 0, end = buf.length) {
  const out = [];
  while (pos < end) {
    const tag = buf[pos];
    let len = buf[pos + 1], head = 2;
    if (len & 0x80) { const n = len & 0x7f; len = 0; for (let i = 0; i < n; i++) len = len * 256 + buf[pos + 2 + i]; head = 2 + n; }
    const start = pos + head;
    if (start + len > end) throw new Error("DER: вихід за межі");
    const node = { tag, body: buf.subarray(start, start + len) };
    if (tag & 0x20) node.children = der(buf, start, start + len);
    out.push(node);
    pos = start + len;
  }
  return out;
}
const oid = (b) => { const o = [Math.floor(b[0] / 40), b[0] % 40]; let v = 0; for (const x of b.subarray(1)) { v = v * 128 + (x & 127); if (!(x & 128)) { o.push(v); v = 0; } } return o.join("."); };
const int = (b) => b.reduce((a, x) => a * 256 + x, 0);

// Усі EncryptedPrivateKeyInfo з ДСТУ-шифруванням: { salt, iters, iv, data }
function shroudedKeys(buf) {
  const found = [];
  const visit = (nodes) => {
    for (const n of nodes) {
      const c = n.children;
      if (n.tag === 0x30 && c?.[0]?.tag === 0x06 && oid(c[0].body) === OID.shroudedKeyBag) {
        const [alg, data] = c[1].children[0].children; // [0] EXPLICIT → EncryptedPrivateKeyInfo
        if (oid(alg.children[0].body) !== OID.pbes2) continue;
        const [kdf, enc] = alg.children[1].children;
        if (oid(kdf.children[0].body) !== OID.pbkdf2 || oid(enc.children[0].body) !== OID.kalyna256Cbc) continue;
        const p = kdf.children[1].children, prf = p.find((x) => x.tag === 0x30);
        if (!prf || oid(prf.children[0].body) !== OID.kmacKupyna256) continue;
        const ivNode = enc.children[1];
        found.push({ salt: p[0].body, iters: int(p[1].body), iv: ivNode.tag === 0x04 ? ivNode.body : ivNode.children[0].body, data: data.body });
        continue;
      }
      if (c) visit(c);
      // Вкладені PKCS#7 data: OCTET STRING з DER усередині
      else if (n.tag === 0x04 && n.body[0] === 0x30) { try { visit(der(n.body)); } catch { /* не DER */ } }
    }
  };
  visit(der(buf));
  return found;
}

// PBKDF2 як у UAPKI (cm-pkcs12/src/crypto/pkcs5.c, pbkdf2_dstu7564kmac): PRF — KMAC «Купина»-256 (ДСТУ 7564), не HMAC;
// ключ KMAC — пароль, доповнений нулями (або обрізаний) до 32 байт. Один блок: ключ «Калини»-256 — рівно 32 байти
export function pbkdf2(password, salt, iters) {
  const key = new Uint8Array(32); key.set(password.subarray(0, 32));
  const mac = (m) => new KupynaKMAC256(key).update(m).digest();
  let u = mac(Uint8Array.from([...salt, 0, 0, 0, 1]));
  const t = u.slice();
  for (let i = 1; i < iters; i++) { u = mac(u); for (let j = 0; j < t.length; j++) t[j] ^= u[j]; }
  return t;
}
// Відкритий ключ — DER PrivateKeyInfo: обрізаємо за його довжиною, доповнення (PKCS#7, 0x80 00… ДСТУ 7624 чи нулі) лише
// звіряємо. Заразом це перевірка пароля: з чужим ключем розшифровується шум, який не складається в PKCS#8
function privateKeyInfo(b) {
  if (b[0] !== 0x30) return null;
  let len = b[1], head = 2;
  if (len & 0x80) { const n = len & 0x7f; if (n > 2) return null; len = 0; for (let i = 0; i < n; i++) len = len * 256 + b[2 + i]; head = 2 + n; }
  const size = head + len, tail = b.subarray(size);
  if (size > b.length || tail.length > 32) return null;
  const okPad = tail.every((x) => x === tail.length) || (tail[0] === 0x80 && tail.subarray(1).every((x) => !x)) || tail.every((x) => !x);
  if (!okPad) return null;
  try {
    const [version, alg, key] = der(b, head, size);
    return version?.tag === 0x02 && alg?.tag === 0x30 && key?.tag === 0x04 ? b.subarray(0, size) : null;
  } catch { return null; }
}

export const kupynaKeyId = (pubBytes) => Kupyna256.create().update(pubBytes).digest();

export const isDstuPfx = (buf) => { try { return shroudedKeys(buf).length > 0; } catch { return false; } };

// Повертає відкриті PKCS#8 (Uint8Array[]); порожній масив — не цей формат; помилка — пароль не підходить
export function decryptDstuPfx(buf, password) {
  let keys;
  try { keys = shroudedKeys(buf); } catch { return []; }
  if (!keys.length) return [];
  const pw = new TextEncoder().encode(password);
  return keys.map(({ salt, iters, iv, data }) => {
    const plain = privateKeyInfo(decryptCBC(new Kalyna256(pbkdf2(pw, salt, iters)), data, iv));
    if (!plain) throw new Error("Неправильний пароль ключа");
    return plain;
  });
}
